/*
 * Log.ts
 * ===========
 * Log to console utility.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import inspect from "browser-util-inspect";
import { makeAutoObservable, observable } from "mobx";
class LogColor {
    static Reset = "\x1b[0m";
    static Bright = "\x1b[1m";
    static Dim = "\x1b[2m";
    static Underscore = "\x1b[4m";
    static Blink = "\x1b[5m";
    static Reverse = "\x1b[7m";
    static Hidden = "\x1b[8m";
    static FgBlack = "\x1b[30m";
    static FgRed = "\x1b[31m";
    static FgGreen = "\x1b[32m";
    static FgYellow = "\x1b[33m";
    static FgBlue = "\x1b[34m";
    static FgMagenta = "\x1b[35m";
    static FgCyan = "\x1b[36m";
    static FgWhite = "\x1b[97m";
    static FgLightGray = "\x1b[37m";
    static FgDarkGray = "\x1b[90m";
    static BgBlack = "\x1b[40m";
    static BgRed = "\x1b[41m";
    static BgGreen = "\x1b[42m";
    static BgYellow = "\x1b[43m";
    static BgBlue = "\x1b[44m";
    static BgMagenta = "\x1b[45m";
    static BgCyan = "\x1b[46m";
    static BgWhite = "\x1b[107m";
    static BgLightGray = "\x1b[47m";
    static BgDarkGray = "\x1b[100m";
    static dim(text) {
        return `${LogColor.Dim}${text}${LogColor.Reset}`;
    }
    static white(text) {
        return `${LogColor.FgWhite}${text}${LogColor.Reset}`;
    }
    static red(text) {
        return `${LogColor.FgRed}${text}${LogColor.Reset}`;
    }
    static yellow(text) {
        return `${LogColor.FgYellow}${text}${LogColor.Reset}`;
    }
    static green(text) {
        return `${LogColor.FgGreen}${text}${LogColor.Reset}`;
    }
    static blue(text) {
        return `${LogColor.FgBlue}${text}${LogColor.Reset}`;
    }
    static lightGray(text) {
        return `${LogColor.FgLightGray}${text}${LogColor.Reset}`;
    }
    static darkGray(text) {
        return `${LogColor.FgDarkGray}${text}${LogColor.Reset}`;
    }
    static magenta(text) {
        return `${LogColor.FgMagenta}${text}${LogColor.Reset}`;
    }
    static cyan(text) {
        return `${LogColor.FgCyan}${text}${LogColor.Reset}`;
    }
    static brightCyan(text) {
        return `${LogColor.FgCyan}${LogColor.Bright}${text}${LogColor.Reset}`;
    }
    static brightRed(text) {
        return `${LogColor.FgRed}${LogColor.Bright}${text}${LogColor.Reset}`;
    }
    static none(text) {
        return text;
    }
}
class LogLevel {
    label;
    value;
    color;
    colorName;
    static Fatal = {
        label: "Fatal",
        value: 0,
        color: LogColor.brightRed,
        colorName: "red"
    };
    static Error = {
        label: "Error",
        value: 1,
        color: LogColor.red,
        colorName: "red"
    };
    static Warn = {
        label: "Warn",
        value: 2,
        color: LogColor.yellow,
        colorName: "yellow"
    };
    static Info = {
        label: "Info",
        value: 3,
        color: LogColor.white,
        colorName: "white"
    };
    static Debug = {
        label: "Debug",
        value: 4,
        color: LogColor.none,
        colorName: undefined
    };
    static Trace = {
        label: "Trace",
        value: 5,
        color: LogColor.lightGray,
        colorName: "lightGray"
    };
    static AllLevels = [
        LogLevel.Fatal,
        LogLevel.Error,
        LogLevel.Warn,
        LogLevel.Info,
        LogLevel.Debug,
        LogLevel.Trace,
    ];
    static {
        inspect.colors["lightGray"] = [37, 39];
        inspect.colors["white"] = [97, 39];
    }
}
export default class Log {
    defaultLogLevel = LogLevel.Info;
    logLevelsByTag = {};
    constructor() {
        makeAutoObservable(this);
    }
    static logger = new Log(); // singleton instance
    // Sets it as the baseline for the whole environment. These are overridden by tag-specific ones
    static setDefaultLogLevel(logLevel) {
        Log.logger.defaultLogLevel = logLevel;
    }
    // Sets the log level for only a specific tag
    static setLogLevel(tag, logLevel) {
        Log.logger.logLevelsByTag[tag] = logLevel;
    }
    static getDefaultLogLevel() {
        return Log.logger.defaultLogLevel;
    }
    static getLogLevel(tag) {
        return Log.logger.getLogLevel(tag);
    }
    getLogLevel(tag) {
        return this.logLevelsByTag[tag] || this.defaultLogLevel;
    }
    trace(tag, obj) {
        const logLevel = this.getLogLevel(tag);
        if (logLevel.value < LogLevel.Trace.value) {
            return;
        }
        this.logMessage(LogLevel.Trace, tag, obj);
    }
    debug(tag, obj) {
        const logLevel = this.getLogLevel(tag);
        if (logLevel.value < LogLevel.Debug.value) {
            return;
        }
        this.logMessage(LogLevel.Debug, tag, obj);
    }
    info(tag, obj) {
        const logLevel = this.getLogLevel(tag);
        if (logLevel.value < LogLevel.Info.value) {
            return;
        }
        this.logMessage(LogLevel.Info, tag, obj);
    }
    warn(tag, obj) {
        const logLevel = this.getLogLevel(tag);
        if (logLevel.value < LogLevel.Warn.value) {
            return;
        }
        this.logMessage(LogLevel.Warn, tag, obj);
    }
    error(tag, obj) {
        const logLevel = this.getLogLevel(tag);
        if (logLevel.value < LogLevel.Error.value) {
            return;
        }
        this.logMessage(LogLevel.Error, tag, obj);
    }
    fatal(tag, obj) {
        const logLevel = this.getLogLevel(tag);
        if (logLevel.value < LogLevel.Fatal.value) {
            return;
        }
        this.logMessage(LogLevel.Fatal, tag, obj);
    }
    static trace(tag, obj) {
        Log.logger.trace(tag, obj);
    }
    static debug(tag, obj) {
        Log.logger.debug(tag, obj);
    }
    static info(tag, obj) {
        Log.logger.info(tag, obj);
    }
    static warn(tag, obj) {
        Log.logger.warn(tag, obj);
    }
    static error(tag, obj) {
        Log.logger.error(tag, obj);
    }
    static fatal(tag, obj) {
        Log.logger.fatal(tag, obj);
    }
    // The default behavior is to show every message. To throttle log spam, set suppressDuplicates=true.
    static log(tag, obj) {
        Log.logger.info(tag, obj);
    }
    logMessage(logLevel, tag, obj) {
        const msg = this.getMessage(logLevel, tag, obj);
        if (logLevel.value < LogLevel.Warn.value) {
            console.error(...msg);
        }
        else if (logLevel === LogLevel.Warn) {
            console.warn(...msg);
        }
        else if (logLevel === LogLevel.Info) {
            console.info(...msg);
        }
        else if (logLevel === LogLevel.Trace) {
            // console.trace isn't useful
            console.log(...msg);
        }
        else {
            console.log(...msg);
        }
    }
    getMessage(logLevel, tag, obj) {
        const time = new Date().toISOString();
        let objStr;
        if (typeof obj === "string") {
            objStr = obj;
        }
        else {
            objStr = `${inspect(obj, { depth: Infinity, colors: !this.isSafari() })}`;
            const lines = (objStr.match(/\n/g) || "").length;
            if (lines > 1) {
                objStr = `\n${objStr}`;
            }
        }
        if (this.isBrowser()) {
            return [`%c${time} %c${tag} %c${objStr}`,
                "color:magenta", "color:cyan", `color:${logLevel.colorName}`];
        }
        else {
            const timeStr = LogColor.magenta(time);
            const tagStr = `${LogColor.brightCyan(tag)}`;
            return [`${timeStr} ${tagStr} ${objStr}`];
        }
    }
    inspect(obj) {
        let objMsg;
        if (typeof obj === "string") {
            objMsg = `${obj}`;
        }
        else {
            objMsg = `${inspect(obj, { depth: Infinity, colors: true })}`;
        }
        return objMsg;
    }
    static inspect(obj) {
        return Log.logger.inspect(obj);
    }
    isBrowser() {
        return typeof window === "object";
    }
    isChrome() {
        return this.isBrowser() && navigator.userAgent.toLowerCase().includes("chrome");
    }
    isSafari() {
        return this.isBrowser() && !this.isChrome();
    }
}
__decorate([
    observable
], Log.prototype, "defaultLogLevel", void 0);
__decorate([
    observable
], Log.prototype, "logLevelsByTag", void 0);
export { LogLevel, LogColor };
